import { defineComponent, h, Transition } from 'vue';

import { animate, nullifyTransforms } from '@components/utils';

import type { PropType } from 'vue';

export const CdsTransitionModal = defineComponent({
  name: 'CdsTransitionModal',
  props: {
    target: Object as PropType<HTMLElement>
  },
  setup(props, { slots }) {
    const functions = {
      onBeforeEnter(el: Element) {
        (el as HTMLElement).style.pointerEvents = 'none';
        (el as HTMLElement).style.visibility = 'hidden';
      },
      async onEnter(el: Element, done: () => void) {
        await new Promise((resolve) => requestAnimationFrame(resolve));

        (el as HTMLElement).style.visibility = '';

        const { x, y, sx, sy, speed } = getDimensions(props.target!, el as HTMLElement);

        const animation = animate(
          el,
          [{ transform: `translate(${x}px, ${y}px) scale(${sx}, ${sy})`, opacity: 0 }, {}],
          {
            duration: 225 * speed, // @todo to constant
            easing: 'cubic-bezier(0.0, 0, 0.2, 1)' // @todo use from cds entering easing
          }
        );

        getChildren(el)?.forEach((el) => {
          animate(
            el,
            [
              { opacity: 0 },
              { opacity: 0, offset: 0.33 }, //@todo 0.33 need to constant or what this value?
              {}
            ],
            {
              duration: 225 * speed, // @todo to constant
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)' // @todo use from cds standard easing
            }
          );
        });

        animation.finished.then(() => done());
      },
      onAfterEnter(el: Element) {
        (el as HTMLElement).style.removeProperty('pointer-events');
      },
      onBeforeLeave(el: Element) {
        (el as HTMLElement).style.pointerEvents = 'none';
      },
      async onLeave(el: Element, done: () => void) {
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const { x, y, sx, sy, speed } = getDimensions(props.target!, el as HTMLElement);

        const animation = animate(
          el,
          [{}, { transform: `translate(${x}px, ${y}px) scale(${sx}, ${sy})`, opacity: 0 }],
          {
            duration: 125 * speed, // @todo to constant
            easing: 'cubic-bezier(0.4, 0, 1, 1)' // @todo use from cds leaving easing
          }
        );

        animation.finished.then(() => done());

        getChildren(el)?.forEach((el) => {
          animate(
            el,
            [
              {},
              { opacity: 0, offset: 0.2 }, //@todo 0.2 need to constant or what this value?
              { opacity: 0 }
            ],
            {
              duration: 125 * 2 * speed, // @todo to constant
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)' // @todo use from cds standard easing
            }
          );
        });
      },
      onAfterLeave(el: Element) {
        (el as HTMLElement).style.removeProperty('pointer-events');
      }
    };

    return () => {
      return props.target
        ? h(
            Transition,
            {
              name: 'modal-transition',
              ...functions,
              css: false
            },
            slots
          )
        : h(
            Transition,
            {
              name: 'modal-transition'
            },
            slots
          );
    };
  }
});

function getDimensions(target: HTMLElement, el: HTMLElement) {
  const targetBox = target.getBoundingClientRect();
  const elBox = nullifyTransforms(el);
  const [originX, originY] = getComputedStyle(el)
    .transformOrigin.split(' ')
    .map((value) => parseFloat(value));
  const [anchorSide, anchorOffset] = getComputedStyle(el).getPropertyValue('--cds-overlay-anchor-origin').split(' ');

  let offsetX = targetBox.left + targetBox.width / 2;
  if (anchorSide === 'left' || anchorOffset === 'left') {
    offsetX -= targetBox.width / 2;
  } else if (anchorSide === 'right' || anchorOffset === 'right') {
    offsetX += targetBox.width / 2;
  }

  let offsetY = targetBox.top + targetBox.height / 2;
  if (anchorSide === 'top' || anchorOffset === 'top') {
    offsetY -= targetBox.height / 2;
  } else if (anchorSide === 'bottom' || anchorOffset === 'bottom') {
    offsetY += targetBox.height / 2;
  }

  const tsx = targetBox.width / elBox.width;
  const tsy = targetBox.height / elBox.height;
  const maxs = Math.max(1, tsx, tsy);
  const sx = tsx / maxs || 0;
  const sy = tsy / maxs || 0;

  // Animate elements larger than 12% of the screen area up to 1.5x slower
  const asa = (elBox.width * elBox.height) / (window.innerWidth * window.innerHeight);
  const speed = asa > 0.12 ? Math.min(1.5, (asa - 0.12) * 10 + 1) : 1;

  return {
    x: offsetX - (originX + elBox.left),
    y: offsetY - (originY + elBox.top),
    sx,
    sy,
    speed
  };
}

function getChildren(el: Element) {
  const els = el.querySelector(':scope > .cds-card, :scope > .cds-container, :scope > .cds-list')?.children;
  return els && [...els];
}
